import "./Question.less";

import { ActionButtons, ReorderButtons } from "features/common/ActionIcons";
import { LevelIndicator } from "features/qualityForm/common/LevelIndicator";
import { selectQuestionHelpText } from "features/qualityForm/qualityFormSlice";
import QuestionBody from "features/qualityForm/common/questionComponents/QuestionBody";
import QuestionName from "features/qualityForm/common/questionComponents/QuestionName";
import { QuestionSettingsIndicators } from "features/qualityForm/common/questionComponents/SettingsIndicators";
import { useState } from "react";
import {
    AssessmentFormQuestionTypeEnum,
    SurveyQuestionTypeEnum,
} from "@genesys/cloud-frontend/dist/mjs/models";
import QuestionEditor from "./QuestionEditor";
import {
    DependentIds,
    EntityType,
    initiateQuestionDeletion,
    moveQuestionDownIfValid,
    moveQuestionUpIfValid,
    selectQuestionGroup,
    selectQuestionGroupIdContainingQuestion,
    VisibilityConflictsOnMove,
    selectCanEditForm,
    qualityFormActions,
} from "features/qualityForm/qualityFormSlice";
import { selectQuestion } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { ErrorAction } from "../common/ErrorBox/VisibilityConflictErrorBox";
import { shallowEqual } from "react-redux";
import { VisibilityConflictErrorBoxContainer } from "../common/ErrorBox/VisibilityConflictErrorBoxContainer";

import { FormLocaleProvider } from "features/i18n/useLocale";
import { useAppDispatch, useAppSelector } from "app/hooks";

export function QuestionSummary({
    questionId,
    onEdit,
    onDelete,
}: {
    questionId: string;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const dispatch = useAppDispatch();
    const [visibilityError, setVisibilityError] = useState<{
        visibilityDependentIds: VisibilityConflictsOnMove;
        errorAction: ErrorAction;
    }>();

    const { canMoveUp, canMoveDown } = useAppSelector((state) => {
        const questionGroupId = selectQuestionGroupIdContainingQuestion(
            questionId
        )(state.qualityForm)!;
        const questions =
            selectQuestionGroup(state, questionGroupId)?.questions ?? [];
        const questionPosition = questions?.indexOf(questionId);
        return {
            canMoveUp: questionPosition > 0,
            canMoveDown: questionPosition < questions.length - 1,
        };
    }, shallowEqual);

    const canEditForm = useAppSelector((state) => {
        return selectCanEditForm(state);
    });

    const question = useAppSelector((state) =>
        selectQuestion(state, questionId)
    );

    const displayQuestionBody =
        question &&
        question.type !== SurveyQuestionTypeEnum.FreeTextQuestion &&
        question.type !== AssessmentFormQuestionTypeEnum.FreeTextQuestion;

    const helpText = useAppSelector((state) =>
        selectQuestionHelpText(state, questionId)
    );

    return (
        <VisibilityConflictErrorBoxContainer
            visibilityConflictsOnMove={visibilityError?.visibilityDependentIds}
            clearVisibilityConflicts={() => setVisibilityError(undefined)}
            entityType={EntityType.Question}
            errorAction={visibilityError?.errorAction ?? ErrorAction.MoveUp}
        >
            <div className="QuestionSummary">
                <div className="actions">
                    <LevelIndicator />
                    {canEditForm && (
                        <ReorderButtons
                            onMoveUp={
                                canMoveUp
                                    ? () => {
                                          const visibilityDependentIds =
                                              dispatch(
                                                  moveQuestionUpIfValid(
                                                      questionId
                                                  )
                                              ) as unknown as DependentIds;

                                          setVisibilityError({
                                              visibilityDependentIds,
                                              errorAction: ErrorAction.MoveUp,
                                          });
                                      }
                                    : undefined
                            }
                            onMoveDown={
                                canMoveDown
                                    ? () => {
                                          const visibilityDependentIds =
                                              dispatch(
                                                  moveQuestionDownIfValid(
                                                      questionId
                                                  )
                                              ) as unknown as DependentIds;

                                          setVisibilityError({
                                              visibilityDependentIds,
                                              errorAction: ErrorAction.MoveDown,
                                          });
                                      }
                                    : undefined
                            }
                        />
                    )}
                </div>
                <div className="content">
                    <div className="heading">
                        <div className="title h3">
                            <QuestionName questionId={questionId} />
                        </div>

                        {canEditForm && (
                            <ActionButtons
                                helpText={helpText}
                                onEdit={onEdit}
                                onDelete={() => {
                                    onDelete();
                                }}
                            />
                        )}
                    </div>
                    <div className="indicators">
                        <QuestionSettingsIndicators questionId={questionId} />
                    </div>
                    {displayQuestionBody && (
                        <FormLocaleProvider>
                            <QuestionBody
                                questionId={questionId}
                                isQuestionEditable={false}
                            />
                        </FormLocaleProvider>
                    )}
                </div>
            </div>
        </VisibilityConflictErrorBoxContainer>
    );
}

export function Question({ questionId }: { questionId: string }) {
    const showEditMode = useAppSelector(
        (state) =>
            state.qualityForm.data.questions?.[questionId].showEditMode ?? false
    );

    const dispatch = useAppDispatch();
    const [visibilityDependentIds, setVisibilityDependentIds] =
        useState<DependentIds>();
    const onDelete = () => {
        const visibilityConflicts = dispatch(
            initiateQuestionDeletion(questionId)
        ) as unknown as DependentIds;
        if (
            visibilityDependentIds?.questionGroupIds.length !== 0 ||
            visibilityDependentIds?.questionIds.length !== 0
        ) {
            setVisibilityDependentIds(visibilityConflicts);
        }
    };

    return (
        <VisibilityConflictErrorBoxContainer
            visibilityDependentIds={visibilityDependentIds}
            clearVisibilityConflicts={() =>
                setVisibilityDependentIds(undefined)
            }
            entityType={EntityType.Question}
            errorAction={ErrorAction.Delete}
        >
            <div className="Question">
                {showEditMode ? (
                    <QuestionEditor
                        questionId={questionId}
                        onDone={() => {
                            dispatch(
                                qualityFormActions.switchEditModeForQuestion({
                                    questionId,
                                    showEditMode: false,
                                })
                            );
                        }}
                        onDelete={onDelete}
                    />
                ) : (
                    <QuestionSummary
                        questionId={questionId}
                        onEdit={() =>
                            dispatch(
                                qualityFormActions.switchEditModeForQuestion({
                                    questionId,
                                    showEditMode: true,
                                })
                            )
                        }
                        onDelete={onDelete}
                    />
                )}
            </div>
        </VisibilityConflictErrorBoxContainer>
    );
}
